'use client'

import Image from 'next/image'
import React, { useState } from 'react'
import { useLocale } from '@/components/LocaleContext'

export type ProductDetailSectionsProps = {
  dimensionLabel?: string | null
  outerDiameter?: string | null
  innerDiameter?: string | null
  overallHeight?: string | null
  threadSize?: string | null
  centerTubeDiameter?: string | null
  micronRating?: number | string | null
  filtrationEfficiency?: string | null
  physicalDimensionsImageUrl?: string | null
  ccmVsCleaningEfficiency?: string | null
  ccmVsCleaningEfficiencyImageUrl?: string | null
  vehicleApplications?: Array<{
    modelType?: string | null
    filterType?: string | null
    engineCode?: string | null
    ccm?: string | null
    kw?: string | null
    hp?: string | null
    year?: string | null
    machineMake?: string | null
    machineModel?: string | null
    equipmentMake?: string | null
    equipmentModel?: string | null
  }> | null
  legalInformations?: string | null
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-5 w-5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const empty = (v: string | null | undefined) => v == null || String(v).trim() === ''

export function ProductDetailSections({
  dimensionLabel,
  outerDiameter,
  innerDiameter,
  overallHeight,
  physicalDimensionsImageUrl,
  threadSize,
  centerTubeDiameter,
  micronRating,
  filtrationEfficiency,
  vehicleApplications,
  legalInformations,
}: ProductDetailSectionsProps) {
  const { locale, translatedData } = useLocale()
  const fallbackByLocale: Record<
    string,
    {
      attributes: string
      outerDiameter: string
      innerDiameter: string
      overallHeight: string
      threadSize: string
      centerTubeDiameter: string
      micronRating: string
      filtrationEfficiency: string
      ccm: string
      kw: string
      hp: string
      noDataAvailable: string
      physicalDimensionsDiagramAlt: string
    }
  > = {
    en: {
      attributes: 'Attributes',
      outerDiameter: 'Outer Diameter(OD)',
      innerDiameter: 'Inner Diameter(ID)',
      overallHeight: 'Overall Height (H)',
      threadSize: 'Thread size (G)',
      centerTubeDiameter: 'Center tube diameter (CTD)',
      micronRating: 'Micron Rating',
      filtrationEfficiency: 'Filtration Efficiency',
      ccm: 'CCM',
      kw: 'kW',
      hp: 'HP',
      noDataAvailable: 'No data available.',
      physicalDimensionsDiagramAlt: 'Physical dimensions diagram',
    },
    fr: {
      attributes: 'Attribut',
      outerDiameter: 'Diametre exterieur(OD)',
      innerDiameter: 'Diametre interieur(ID)',
      overallHeight: 'Hauteur (H)',
      threadSize: 'Taille de filetage (G)',
      centerTubeDiameter: 'Diametre du tube central (CTD)',
      micronRating: 'Indice micronique',
      filtrationEfficiency: 'Efficacite de filtration',
      ccm: 'CCM',
      kw: 'kW',
      hp: 'HP',
      noDataAvailable: 'Aucune donnee disponible.',
      physicalDimensionsDiagramAlt: 'Schema des dimensions physiques',
    },
    ar: {
      attributes: 'السمات',
      outerDiameter: 'القطر الخارجي(OD)',
      innerDiameter: 'القطر الداخلي(ID)',
      overallHeight: 'الارتفاع (H)',
      threadSize: 'حجم الخيط (G)',
      centerTubeDiameter: 'قطر الأنبوب المركزي (CTD)',
      micronRating: 'تقييم الميكرون',
      filtrationEfficiency: 'كفاءة الترشيح ',
      ccm: 'CCM',
      kw: 'kW',
      hp: 'HP',
      noDataAvailable: 'لا توجد بيانات متاحة.',
      physicalDimensionsDiagramAlt: 'رسم توضيحي للأبعاد الفيزيائية',
    },
  }
  const fallback = fallbackByLocale[locale] ?? fallbackByLocale.en
  const [open, setOpen] = useState<Record<string, boolean>>({
    dimensions: true,
    ccm: false,
    vehicle: false,
    oe: false,
    legal: false,
  })

  const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !o[key] }))
  const rows = Array.isArray(vehicleApplications) ? vehicleApplications : []
  const machineApplicationsLabelByLocale: Record<string, string> = {
    en: 'Machine / applications',
    fr: 'Machine / applications',
    ar: 'الآلة / التطبيقات',
  }
  const legacyVehicleLabels = new Set([
    'Vehicle / applications',
    'Véhicule / applications',
    'المركبة / التطبيقات',
  ])
  const configuredVehicleLabel = translatedData?.common.vehicle_applications?.trim()
  const machineApplicationsLabel =
    configuredVehicleLabel && !legacyVehicleLabels.has(configuredVehicleLabel)
      ? configuredVehicleLabel
      : (machineApplicationsLabelByLocale[locale] ?? machineApplicationsLabelByLocale.en)
  const attributeLabel = fallback.attributes
  const formatAttributeValue = (value: string | number, unit?: 'mm' | '%') => {
    const trimmedValue = String(value).trim()
    const cleanedValue = trimmedValue.replace(/[()\[\]{}]/g, '').trim()
    const parsedValue = Number(trimmedValue)

    if (cleanedValue.length === 0) {
      return 'N/A'
    }

    if (!Number.isNaN(parsedValue) && parsedValue === 0) {
      return 'N/A'
    }

    if (unit === '%') {
      const normalized = trimmedValue.replace(/%+$/g, '').trim()
      return `${normalized}%`
    }

    return unit ? `${trimmedValue} ${unit}` : trimmedValue
  }

  return (
    <div className="border-t border-neutral-200 mt-8">
      {/* Physical dimensions */}
      <section className="border-b border-neutral-200">
        <button
          type="button"
          onClick={() => toggle('dimensions')}
          className="flex w-full items-center justify-between py-4 font-poppins text-left font-bold uppercase text-[#111] focus:outline-none"
          aria-expanded={open.dimensions}
        >
          {attributeLabel}
          <ChevronDown open={open.dimensions} />
        </button>
        {open.dimensions && (
          <div className="pb-4 text-neutral-600 font-poppins text-sm space-y-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              {physicalDimensionsImageUrl && (
                <div className="relative w-full max-w-md aspect-[4/3] shrink-0 overflow-hidden sm:max-w-[280px]">
                  <Image
                    src={physicalDimensionsImageUrl}
                    alt={fallback.physicalDimensionsDiagramAlt}
                    fill
                    className="object-contain object-left"
                    sizes="(max-width: 640px) 100vw, 280px"
                    unoptimized={physicalDimensionsImageUrl.startsWith('http')}
                  />
                </div>
              )}
              {(outerDiameter ?? innerDiameter ?? overallHeight) && (
                <ul className="list-none space-y-2 font-semibold text-[#111] min-w-0">
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">{fallback.outerDiameter}</span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] sm:min-w-[calc(2rem+270px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span className="shrink-0">{formatAttributeValue(String(outerDiameter ?? ''), 'mm')}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">{fallback.innerDiameter}</span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(innerDiameter ?? ''), 'mm')}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">{fallback.overallHeight}</span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(overallHeight ?? ''), 'mm')}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">
                      {translatedData?.common.thread_size ?? fallback.threadSize}
                    </span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(threadSize ?? ''))}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">
                      {translatedData?.common.center_tube_diameter ?? fallback.centerTubeDiameter}
                    </span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(centerTubeDiameter ?? ''), 'mm')}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">{fallback.micronRating}</span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(micronRating ?? ''))}</span>
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="uppercase">{fallback.filtrationEfficiency}</span>
                    <span
                      className="h-px flex-1 min-w-[calc(2rem+100px)] self-center bg-[radial-gradient(circle,_#d4d4d4_1px,_transparent_1.5px)] bg-[length:4px_1px] bg-repeat-x"
                      aria-hidden
                    />
                    <span>{formatAttributeValue(String(filtrationEfficiency ?? ''), '%')}</span>
                  </li>
                </ul>
              )}
            </div>
            {empty(dimensionLabel) &&
              !physicalDimensionsImageUrl &&
              !outerDiameter &&
              !innerDiameter &&
              !overallHeight && (
                <p>{translatedData?.common.no_data_available ?? fallback.noDataAvailable}</p>
              )}
          </div>
        )}
      </section>

      {/* CCM vs cleaning efficiency */}
      {/* <section className="border-b border-neutral-200">
        <button
          type="button"
          onClick={() => toggle('ccm')}
          className="flex w-full items-center justify-between py-4 font-poppins text-left font-semibold uppercase text-[#111] focus:outline-none"
          aria-expanded={open.ccm}
        >
          CCM vs cleaning efficiency
          <ChevronDown open={open.ccm} />
        </button>
        {open.ccm && (
          <div className="pb-4 text-neutral-600 font-poppins text-sm space-y-3">
            {ccmVsCleaningEfficiencyImageUrl && (
              <div className="relative w-full max-w-md aspect-[4/3] overflow-hidden">
                <Image
                  src={ccmVsCleaningEfficiencyImageUrl}
                  alt="CCM vs cleaning efficiency chart"
                  fill
                  className="object-contain object-left"
                  sizes="(max-width: 768px) 100vw, 28rem"
                  unoptimized={ccmVsCleaningEfficiencyImageUrl.startsWith('http')}
                />
              </div>
            )}
            {!empty(ccmVsCleaningEfficiency) && <p className="whitespace-pre-wrap">{ccmVsCleaningEfficiency}</p>}
            {empty(ccmVsCleaningEfficiency) && !ccmVsCleaningEfficiencyImageUrl && <p>No data available.</p>}
          </div>
        )}
      </section> */}

      {/* Machine / applications */}
      <section className="border-b border-neutral-200">
        <button
          type="button"
          onClick={() => toggle('vehicle')}
          className="flex w-full items-center justify-between py-4 font-poppins text-left font-semibold uppercase text-[#111] focus:outline-none"
          aria-expanded={open.vehicle}
        >
          {machineApplicationsLabel}
          <ChevronDown open={open.vehicle} />
        </button>
        {open.vehicle && (
          <div className="pb-4 text-neutral-600 font-poppins text-sm overflow-x-auto">
            {rows.length === 0 ? (
              <p>{translatedData?.common.no_data_available ?? fallback.noDataAvailable}</p>
            ) : (
              <table className="w-full min-w-[500px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-neutral-300">
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.model_type ?? 'Model type'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.filter_type ?? 'Filter type'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.engine_code ?? 'Engine code'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">{fallback.ccm}</th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">{fallback.kw}</th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">{fallback.hp}</th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.year ?? 'Year'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.machine_make ?? 'Machine make'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.machine_model ?? 'Machine model'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.equipment_make ?? 'Equipment make'}
                    </th>
                    <th className="py-2 pr-3 font-semibold text-[#111]">
                      {translatedData?.common.equipment_model ?? 'Equipment model'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-neutral-200">
                      <td className="py-2 pr-3">{row.modelType ?? '—'}</td>
                      <td className="py-2 pr-3">{row.filterType ?? '—'}</td>
                      <td className="py-2 pr-3">{row.engineCode ?? '—'}</td>
                      <td className="py-2 pr-3">{row.ccm ?? '—'}</td>
                      <td className="py-2 pr-3">{row.kw ?? '—'}</td>
                      <td className="py-2 pr-3">{row.hp ?? '—'}</td>
                      <td className="py-2 pr-3">{row.year ?? '—'}</td>
                      <td className="py-2 pr-3">{row.machineMake ?? '—'}</td>
                      <td className="py-2 pr-3">{row.machineModel ?? '—'}</td>
                      <td className="py-2 pr-3">{row.equipmentMake ?? '—'}</td>
                      <td className="py-2 pr-3">{row.equipmentModel ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* Legal informations */}
      {/* <section className="border-b border-neutral-200">
        <button
          type="button"
          onClick={() => toggle('legal')}
          className="flex w-full items-center justify-between py-4 font-poppins text-left font-semibold uppercase text-[#111] focus:outline-none"
          aria-expanded={open.legal}
        >
          {translatedData?.common.legal_informations ?? 'Legal informations'}
          <ChevronDown open={open.legal} />
        </button>
        {open.legal && (
          <div className="pb-4 text-neutral-600 font-poppins text-sm whitespace-pre-wrap">
            {empty(legalInformations) ? (
              <p>{translatedData?.common.no_data_available ?? 'No data available.'}</p>
            ) : (
              <p>{legalInformations}</p>
            )}
          </div>
        )}
      </section> */}
    </div>
  )
}
